import { useCallback, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { FILTER_ITEMS, SORT_ITEMS } from "app/pages/activation/components/Topics/constants";
import type { TopicFilters, TopicSorting } from "app/pages/activation/components/Topics/types";
import { downloadBlob } from "app/pages/activation/helpers/downloadBlob";
import { createActivationApi } from "app/pages/activation/services/api";

import { useDeleteTopicsMutation } from "../../queries/topics/useDeleteTopicsMutation";
import { useUpdateTopicMutation } from "../../queries/topics/useUpdateTopicMutation";
import { activationTracking } from "../../services/tracking";

type UseTopicsSchema = {
    setFilter: (filterId: TopicFilters) => void;
    setSorting: (sorting: { id: TopicSorting; text: string }) => void;
};

export const useTopics = ({ setFilter, setSorting }: UseTopicsSchema) => {
    const {
        params: { country, key: activationId, timeGranularity: granularity },
    } = useCurrentStateAndParams();

    const { mutate: updateTopic } = useUpdateTopicMutation(activationId, granularity);
    const { mutate: deleteTopic } = useDeleteTopicsMutation();
    const [downloadingTopics, setDownloadingTopics] = useState<string[]>([]);

    const track = activationTracking.home;
    const api = createActivationApi();

    const onFilter = useCallback(
        (filterId: TopicFilters) => {
            setFilter(filterId);
            const textForTracking = FILTER_ITEMS.find((item) => filterId === item.id)?.text;
            track.trackTopicFilter(textForTracking);
        },
        [setFilter, track],
    );

    const onSort = useCallback(
        ({ id }: { id: TopicSorting }) => {
            const selected = SORT_ITEMS.find((i) => i.id === id);
            setSorting(selected);
            const textForTracking = SORT_ITEMS.find((item) => id === item.id)?.text;
            track.trackTopicSort(textForTracking);
        },
        [setSorting, track],
    );

    const onDownloadExcel = useCallback(
        async (topicId: string, keywordsCount: number) => {
            setDownloadingTopics((prev) => [...prev, topicId]);
            track.trackUserClickDownloadPDFTopic(keywordsCount);

            const body = {
                country,
                KeywordsGroup: topicId,
            };

            try {
                const { blob, filename } = await api.topics.downloadExcel(body);

                downloadBlob(blob, filename);
            } catch (error) {
                console.error("Excel download error:", error);
            } finally {
                setDownloadingTopics((prev) => prev.filter((id) => id !== topicId));
            }
        },
        [api.topics, country, track],
    );

    return {
        onSort,
        onFilter,
        onDownloadExcel,
        updateTopic,
        deleteTopic,
        downloadingTopics,
    };
};
